import { CircleInfoIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ReactNode } from "react";
import { SIDEBAR_OFFSET } from "@/utils/sidebar";

interface ContentLayoutProps {
    isWide?: boolean;
    isDisabled?: boolean;
    className?: string;
    classNames?: { root?: string };
    children: ReactNode;
}

export const ContentLayout = ({
    isWide = false,
    isDisabled = false,
    className,
    classNames,
    children,
}: ContentLayoutProps) => {
    return (
        // The padding classes are from the HeaderToolbar component of UI Kit. The left & padding must match that
        <fieldset className={clsx("mb-20", "px-5 py-3 pr-3 xl:px-10 xl:py-5", classNames?.root)} disabled={isDisabled}>
            <div className={clsx(className, isWide || "max-w-[560px]")}>{children}</div>
        </fieldset>
    );
};

interface FooterProps {
    preferenceSidebar?: boolean;
    className?: string;
    children: ReactNode;
}

const Footer = ({ preferenceSidebar = false, className, children }: FooterProps) => {
    return (
        <div
            // The sidebar is resizable, so its live width is the only reliable offset: a fixed
            // margin leaves the first button buried under a widened sidebar.
            style={{ marginLeft: SIDEBAR_OFFSET }}
            className={clsx(
                "fixed bottom-0 left-0 right-0 space-x-4 border-t border-t-gray-light bg-white px-5 py-4 xl:px-10",
                // The preference sidebar only sits beside the content from `lg` up, so it is the
                // only breakpoint range where the footer has to clear it too.
                preferenceSidebar && "lg:left-64 xl:left-75",
                className,
            )}
        >
            {children}
        </div>
    );
};

export interface SectionProps {
    children: ReactNode | React.ReactElement;
    title: string;
    description?: React.ReactNode;
    tooltip?: string | React.ReactNode;
    classNames?: { title?: string; content?: string };
}

interface TitleProps {
    title: string;
    description?: React.ReactNode;
    className?: string;
    tooltip?: string | React.ReactNode;
}

const Section = ({ title, description, tooltip, classNames, children }: SectionProps) => {
    return (
        <div
            className="flex flex-col gap-4 py-12 lg:flex-row lg:gap-x-10"
            data-testid={`content-layout-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <Title title={title} description={description} tooltip={tooltip} className={classNames?.title} />
            {/** The viewport caps only apply once the columns sit side by side */}
            <div
                className={clsx("w-full min-w-0 lg:max-w-[45vw] xl:max-w-[35vw] 2xl:max-w-[45vw]", classNames?.content)}
            >
                {children}
            </div>
        </div>
    );
};

const Title = ({ title, description = "", tooltip, className }: TitleProps) => {
    return (
        <div className={clsx("lg:w-44 xl:w-64", className)}>
            <h3 className="leading-p1 tracking-tightest">
                {title}
                {tooltip && (
                    <Tooltip className="ml-1" content={tooltip}>
                        <CircleInfoIcon />
                    </Tooltip>
                )}
            </h3>
            <p className="text-gray-darker">{description}</p>
        </div>
    );
};

ContentLayout.Footer = Footer;
ContentLayout.Section = Section;
ContentLayout.Title = Title;
